import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  verifyBackofficePassword,
  getBackofficeToken,
  setBackofficeToken,
  clearBackofficeToken,
} from '@api/backoffice';
import BackofficeContext from './BackofficeContextStore';

export function BackofficeProvider({ children }: { children: ReactNode }) {
  const [isVerified, setIsVerified] = useState(() => !!getBackofficeToken());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = getBackofficeToken();
      if (!token && isVerified) {
        setIsVerified(false);
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [isVerified]);

  const verify = useCallback(async (password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await verifyBackofficePassword(password);
      setBackofficeToken(result.token, result.expiresInSeconds);
      setIsVerified(true);
      return true;
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Invalid password';
      setError(message);
      setIsVerified(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearBackofficeToken();
    setIsVerified(false);
  }, []);

  const value = useMemo(
    () => ({ isVerified, isLoading, error, verify, logout }),
    [isVerified, isLoading, error, verify, logout]
  );

  return (
    <BackofficeContext.Provider value={value}>
      {children}
    </BackofficeContext.Provider>
  );
}
